import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ChevronDownIcon,
    ChevronUpIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

// Desktop Orders Table Component
const DesktopOrdersTable = ({
                                orders,
                                sortConfig,
                                requestSort,
                                getStatusBadgeColor,
                                handleDeleteClick,
                                hasRole,
                                expandedRows,
                                toggleRowExpansion
                            }) => {
    return (
        <div className="hidden md:block bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="w-10 px-2 py-3"></th> {/* Expansion column */}
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => requestSort('order_number')}
                        >
                            <div className="flex items-center">
                                Order #
                                {sortConfig.key === 'order_number' && (
                                    <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </div>
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => requestSort('order_date')}
                        >
                            <div className="flex items-center">
                                Date
                                {sortConfig.key === 'order_date' && (
                                    <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </div>
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => requestSort('customer_name')}
                        >
                            <div className="flex items-center">
                                Customer
                                {sortConfig.key === 'customer_name' && (
                                    <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </div>
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => requestSort('status')}
                        >
                            <div className="flex items-center">
                                Status
                                {sortConfig.key === 'status' && (
                                    <span className="ml-1">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                )}
                            </div>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Items
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {orders.length > 0 ? (
                        orders.map((order) => (
                            <React.Fragment key={order.id}>
                                <tr className={`${expandedRows.includes(order.id) ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                                    <td className="px-2 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => toggleRowExpansion(order.id)}
                                            className="text-gray-500 hover:text-gray-700"
                                        >
                                            {expandedRows.includes(order.id) ? (
                                                <ChevronUpIcon className="h-5 w-5" />
                                            ) : (
                                                 <ChevronDownIcon className="h-5 w-5" />
                                             )}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-primary-600">
                                            <Link to={`/orders/${order.id}`} className="hover:underline">
                                                {order.order_number}
                                            </Link>
                                            {order.shopify_id && (
                                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                        Shopify
                                                    </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {order.items && order.items.map((item, idx) => (
                                                <div key={idx} className="truncate max-w-xs">
                                                    {item.sku || 'No SKU'}
                                                </div>
                                            )).slice(0, 2)}
                                            {order.items && order.items.length > 2 && (
                                                <div className="text-xs text-gray-400">
                                                    +{order.items.length - 2} more
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {moment(order.order_date).format('MMM D, YYYY h:mm A')}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {order.customer_name || 'Unknown Customer'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(order.status)}`}>
                                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {order.items?.length || 0} items
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end space-x-2">
                                            <Link
                                                to={`/orders/${order.id}`}
                                                className="text-primary-600 hover:text-primary-900"
                                                title="View"
                                            >
                                                <EyeIcon className="h-5 w-5" />
                                            </Link>

                                            {hasRole('admin') && (
                                                <>
                                                    <Link
                                                        to={`/orders/${order.id}/edit`}
                                                        className="text-indigo-600 hover:text-indigo-900"
                                                        title="Edit"
                                                    >
                                                        <PencilIcon className="h-5 w-5" />
                                                    </Link>

                                                    <button
                                                        onClick={() => handleDeleteClick(order)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="Delete"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {expandedRows.includes(order.id) && (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-4 bg-gray-50">
                                            <div className="border-t border-gray-200 pt-4">
                                                <h4 className="text-sm font-medium text-gray-900 mb-2">Order Details</h4>
                                                <div className="grid grid-cols-3 gap-4">
                                                    <div>
                                                        <p className="text-xs font-medium text-gray-500">Ship To:</p>
                                                        {order.ship_to && (
                                                            <div className="text-sm">
                                                                <p>{order.ship_to.name}</p>
                                                                <p>{order.ship_to.street1}</p>
                                                                {order.ship_to.street2 && <p>{order.ship_to.street2}</p>}
                                                                <p>{order.ship_to.city}, {order.ship_to.state} {order.ship_to.postalCode}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-medium text-gray-500">Order Total:</p>
                                                        <p className="text-sm">${parseFloat(order.order_total).toFixed(2)}</p>
                                                        <p className="text-xs font-medium text-gray-500 mt-2">Payment Method:</p>
                                                        <p className="text-sm">{order.payment_method || 'Not specified'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-medium text-gray-500">Items:</p>
                                                        <div className="mt-1 space-y-1">
                                                            {order.items && order.items.map((item, idx) => (
                                                                <div key={idx} className="flex justify-between text-sm">
                                                                    <span className="font-medium">{item.sku || item.name}</span>
                                                                    <span>{item.quantity}x ${parseFloat(item.unit_price).toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))
                    ) : (
                         <tr>
                             <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                                 No orders found.
                             </td>
                         </tr>
                     )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DesktopOrdersTable;



